import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, type SupportedLang } from '../lib/i18n';
import { apiFetch } from '../lib/api';

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  // i18n.language can be a region-tagged variant like "en-US"; collapse it
  // back to the base supported lang so the dropdown highlights correctly.
  const active = (
    SUPPORTED_LANGS.find((l) => i18n.language === l || i18n.language?.startsWith(`${l}-`)) ?? 'he'
  ) as SupportedLang;

  async function pick(lang: SupportedLang) {
    if (lang !== active) {
      await i18n.changeLanguage(lang);
      // Persist server-side so the AppShell sync-from-me effect doesn't
      // flip it back on next /api/me refresh. Failure is non-fatal —
      // the SPA still uses the new language locally.
      void apiFetch('/api/me', {
        method: 'PATCH',
        body: JSON.stringify({ defaultLocale: lang }),
      }).catch(() => {});
    }
    setOpen(false);
  }

  return (
    <div ref={ref} className="lang-switcher" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="lang-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.label')}
      >
        {t(`language.${active}_short`)}
      </button>
      {open && (
        <div className="lang-menu" role="listbox">
          {SUPPORTED_LANGS.map((l) => (
            <div
              key={l}
              role="option"
              aria-selected={l === active}
              className={`lang-menu-item ${l === active ? 'active' : ''}`}
              onClick={() => void pick(l)}
            >
              <span className="lang-code">{t(`language.${l}_short`)}</span>
              <span className="lang-name">{t(`language.${l}`)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
